import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, CheckCheck, X } from "lucide-react";
import { API, useAuth } from "../../App";

const POLL_INTERVAL_MS = 30_000;

/**
 * Admin bell-icon notification panel.
 *
 * Polls /admin/notifications every 30s. Counter shows unread count. Click to
 * open a dropdown of the latest 20 notifications; each item links to its lead.
 * Marks individual or all-as-read.
 */
const NotificationBell = ({ variant = "default" }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const panelRef = useRef(null);

  const fetchOnce = useMemo(() => async () => {
    try {
      const r = await axios.get(`${API}/admin/notifications?limit=20`, { headers });
      setUnread(r.data.unread_count ?? 0);
      setItems(r.data.items ?? []);
    } catch {
      // Silent — bell is non-critical UI.
    }
  }, [headers]);

  useEffect(() => {
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchOnce]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/admin/notifications/${id}/read`, {}, { headers });
      setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((c) => Math.max(0, c - 1));
    } catch { /* noop */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/read-all`, {}, { headers });
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* noop */ }
  };

  const onItemClick = (n) => {
    setOpen(false);
    if (!n.read) markRead(n.id);
    if (n.lead_id) navigate(`/admin/leads?focus=${n.lead_id}`);
  };

  return (
    <div className="relative" ref={panelRef} data-testid={`notification-bell-${variant}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full hover:bg-slate-100 transition"
        data-testid="bell-toggle"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {unread > 0 && (
          <span
            data-testid="bell-counter"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DE6E4B] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#1F2A44] text-white">
            <span className="text-sm font-bold">Notifications</span>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  data-testid="mark-all-read"
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                >
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-white/10"
                data-testid="close-notifications"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <ul className="max-h-96 overflow-y-auto divide-y divide-slate-100">
            {items.length === 0 && (
              <li className="p-6 text-center text-sm text-slate-400" data-testid="no-notifications">
                You're all caught up.
              </li>
            )}
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => onItemClick(n)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${
                    n.read ? "opacity-60" : ""
                  }`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="w-2 h-2 mt-1.5 rounded-full bg-[#DE6E4B] flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-[#1F2A44] truncate">{n.title}</p>
                      {n.body && (
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wide">
                        {n.kind?.replace(/_/g, " ")}
                      </p>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
